import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import PillsInput from './PillsInput';
import SubThread from './SubThread';
import Duets from './Duets';
import VideoSearch from './VideoSearch';
import KeywordsInput from './KeywordsInput';
import BusinessDropdown from './BusinessDropdown';
import GooglePlacesInput from './GooglePlacesInput';

const ReactForm = ({ 
  onSubmit, 
  fields, 
  submitText = 'Submit',
  loading = false,
  error = null,
  defaultValues = {}
}) => {
  const { register, handleSubmit, formState: { errors }, watch, setValue, getValues, control } = useForm({
    defaultValues,
    mode: 'onChange'
  });

  const watchFields = watch();

  // State to manage blob URLs for file previews - using useRef to avoid re-renders
  const fileBlobUrls = React.useRef(new Map());

  // Helper function to get or create blob URL for a file
  const getFileBlobUrl = React.useCallback((file, fieldName, index) => {
    if (!(file instanceof File)) return null;
    
    const key = `${fieldName}-${index}-${file.name}-${file.size}`;
    
    if (!fileBlobUrls.current.has(key)) {
      const blobUrl = URL.createObjectURL(file);
      fileBlobUrls.current.set(key, blobUrl);
    }
    
    return fileBlobUrls.current.get(key);
  }, []);

  // Clean up function for removing blob URLs
  const cleanupBlobUrl = React.useCallback((file, fieldName, index) => {
    if (!(file instanceof File)) return;
    
    const key = `${fieldName}-${index}-${file.name}-${file.size}`;
    const blobUrl = fileBlobUrls.current.get(key);
    
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      fileBlobUrls.current.delete(key);
    }
  }, []);

  // Clean up all blob URLs when component unmounts
  React.useEffect(() => {
    return () => {
      fileBlobUrls.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      fileBlobUrls.current.clear();
    };
  }, []);

  const submitHandler = (data) => {
    // Remove sensitive data from console logs
    const sanitizedData = { ...data };
    if (sanitizedData.password) {
      sanitizedData.password = '[REDACTED]';
    }
    console.log('ReactForm submitHandler received data:', sanitizedData);
    onSubmit(data);
  };

  const renderField = (field) => {
    const {
      name,
      label,
      type = 'text',
      required = false,
      placeholder = '',
      options = [],
      multiple = false,
      min,
      max,
      step,
      accept,
      onChange,
      component,
      showPassword,
      onTogglePassword,
      preview,
      googlePlacesApiKey,
      onPlaceSelect
    } = field;

    if (type === 'custom') {
      return component || null;
    }

    if (type === 'tiktok-search' || type === 'dropdown') {
      if (component) {
        // Preserve the component's existing onChange if it exists
        const existingOnChange = component.props?.onChange;
        
        const props = {
          control,
          name,
          required,
          onChange: (value) => {
            // Call the component's existing onChange first (for state management)
            if (existingOnChange) {
              existingOnChange(value);
            }
            
            // Then update the form value
            const currentValue = watch(name);
            if (JSON.stringify(currentValue) !== JSON.stringify(value)) {
              setValue(name, value, { shouldValidate: true });
              if (onChange) onChange(value);
            }
          }
        };

        return React.cloneElement(component, props);
      }
      return null;
    }

    const commonProps = {
      ...register(name, { 
        required: required ? `${label} is required` : false,
        min: min !== undefined ? { value: min, message: `Minimum value is ${min}` } : undefined,
        max: max !== undefined ? { value: max, message: `Maximum value is ${max}` } : undefined,
        onChange: (e) => {
          if (onChange) {
            onChange(e);
          }
        }
      }),
      className: `border p-2 w-full rounded ${errors[name] ? 'border-red-500' : 'border-gray-300'}`,
      placeholder,
      id: name
    };

    switch (type) {
      case 'textarea':
        return (
          <textarea
            {...commonProps}
            rows={4}
          />
        );

      case 'password':
        return (
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              {...commonProps}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-600"
              onClick={() => {
                if (onTogglePassword) {
                  onTogglePassword();
                }
              }}
            >
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
        );

      case 'google-places':
        return (
          <Controller
            control={control}
            name={name}
            rules={{ required: required ? `${label} is required` : false }}
            render={({ field: formField }) => (
              <GooglePlacesInput
                value={formField.value || ''}
                onChange={formField.onChange}
                placeholder={placeholder}
                apiKey={googlePlacesApiKey}
                hasError={!!errors[name]}
                onPlaceSelect={(placeDetails) => {
                  if (onPlaceSelect) {
                    onPlaceSelect(placeDetails, { setValue, getValues });
                  }
                }}
              />
            )}
          />
        );

      case 'select':
        return (
          <select {...commonProps} multiple={multiple}>
            <option value="">{placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'date':
        return (
          <input
            type="date"
            {...commonProps}
          />
        );

      case 'time':
        return (
          <input
            type="time"
            {...commonProps}
          />
        );
      case 'pills':
        return (
          <Controller
            control={control}
            name={name}
            rules={{ required: required ? `${label} is required` : false }}
            render={({ field }) => (
              <PillsInput
                value={field.value || []}
                onChange={field.onChange}
                placeholder={placeholder}
              />
            )}
          />
        );

      case 'sub-thread':
        return (
          <Controller
            control={control}
            name={name}
            rules={{ required: required ? `${label} is required` : false }}
            render={({ field }) => (
              <div>
                <h2 className="text-xl font-bold mb-4">{label}</h2>
                {(field.value || []).map((subThread, index) => (
                  <SubThread
                    key={index}
                    index={index}
                    value={subThread}
                    onChange={(index, fieldName, value) => {
                      const newValue = [...(field.value || [])];
                      newValue[index] = { ...newValue[index], [fieldName]: value };
                      field.onChange(newValue);
                    }}
                    onRemove={(index) => {
                      const newValue = (field.value || []).filter((_, i) => i !== index);
                      field.onChange(newValue);
                    }}
                    onVideoSearch={field.onVideoSearch}
                    onDownloadVideo={field.onDownloadVideo}
                    isSearching={field.isSearching}
                    pendingDownloads={field.pendingDownloads}
                    downloadedVideos={field.downloadedVideos}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => {
                    const newValue = [...(field.value || [])];
                    newValue.push({
                      title: "",
                      bookingLink: "",
                      video: "",
                      rating: 0,
                      minRate: 0,
                      maxRate: 0,
                    });
                    field.onChange(newValue);
                  }}
                  className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Add Sub-thread
                </button>
              </div>
            )}
          />
        );

        case 'duets':
          const {
            onVideoSearch,
            onDownloadVideo,
            isSearching,
            pendingDownloads,
            downloadedVideos,
            existingDuets
          } = field;
          
          return (
            <Controller
              control={control}
              name={name}
              rules={{ required: required ? `${label} is required` : false }}
              defaultValue={[]}
              render={({ field: formField }) => (
                <div>
                  {(formField.value || []).map((duet, index) => (
                    <Duets
                      key={index}
                      index={index}
                      value={duet}
                      onChange={(index, fieldName, value) => {
                        // Get current value and update it
                        const currentValue = formField.value || [];
                        const newValue = [...currentValue];
                        if (fieldName === 'batch' && typeof value === 'object') {
                          // Handle batch updates
                          newValue[index] = { ...newValue[index], ...value };
                        } else {
                          // Handle single field update
                          newValue[index] = { ...newValue[index], [fieldName]: value };
                        }
                        formField.onChange(newValue);
                      }}
                      onRemove={(index) => {
                        const currentValue = formField.value || [];
                        const newValue = currentValue.filter((_, i) => i !== index);
                        formField.onChange(newValue);
                      }}
                      onVideoSearch={onVideoSearch}
                      onDownloadVideo={onDownloadVideo}
                      isSearching={isSearching}
                      pendingDownloads={pendingDownloads}
                      downloadedVideos={downloadedVideos}
                      existingDuets={existingDuets}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const currentValue = formField.value || [];
                      const newValue = [...currentValue];
                      newValue.push({
                        video: "",
                        author: "",
                        cover: ""
                      });
                      formField.onChange(newValue);
                    }}
                    className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                  >
                    Add Duet
                  </button>
                </div>
              )}
            />
          );

        case 'video-search':
          const {
            onVideoSearch: videoOnVideoSearch,
            onDownloadVideo: videoOnDownloadVideo,
            isSearching: videoIsSearching,
            pendingDownloads: videoPendingDownloads,
            downloadedVideos: videoDownloadedVideos,
            existingVideos: videoExistingVideos
          } = field;
          
          return (
            <Controller
              control={control}
              name={name}
              rules={{ required: required ? `${label} is required` : false }}
              defaultValue={[]}
              render={({ field: formField }) => (
                <div>
                  {(formField.value || []).map((video, index) => (
                    <VideoSearch
                      key={index}
                      index={index}
                      value={video}
                      onChange={(index, fieldName, value) => {
                        // Get current value and update it
                        const currentValue = formField.value || [];
                        const newValue = [...currentValue];
                        if (fieldName === 'batch' && typeof value === 'object') {
                          // Handle batch updates
                          newValue[index] = { ...newValue[index], ...value };
                        } else {
                          // Handle single field update
                          newValue[index] = { ...newValue[index], [fieldName]: value };
                        }
                        formField.onChange(newValue);
                      }}
                      onRemove={(index) => {
                        const currentValue = formField.value || [];
                        const newValue = currentValue.filter((_, i) => i !== index);
                        formField.onChange(newValue);
                      }}
                      onVideoSearch={videoOnVideoSearch}
                      onDownloadVideo={videoOnDownloadVideo}
                      isSearching={videoIsSearching}
                      pendingDownloads={videoPendingDownloads}
                      downloadedVideos={videoDownloadedVideos}
                      existingVideos={videoExistingVideos}
                      onFileDrop={(idx, files) => {
                        const currentVideos = formField.value || [];
                        const newVideos = files.map(file => ({
                          video: URL.createObjectURL(file), // for preview
                          file,
                          cover: null
                        }));
                        
                        // Update the specific video at the given index
                        const updatedVideos = [...currentVideos];
                        if (newVideos.length > 0) {
                          // Replace the video at the specific index
                          updatedVideos[idx] = {
                            ...updatedVideos[idx],
                            ...newVideos[0] // Only take the first file since we expect one at a time
                          };
                          formField.onChange(updatedVideos);
                        }
                      }}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const currentValue = formField.value || [];
                      const newValue = [...currentValue];
                      newValue.push({
                        video: "",
                        cover: ""
                      });
                      formField.onChange(newValue);
                    }}
                    className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                  >
                    Add Video
                  </button>
                </div>
              )}
            />
          );

      case 'number':
        return (
          <input
            type="number"
            {...commonProps}
            min={min}
            max={max}
            step={step}
          />
        );

      case 'file':
        return (
          <div className="space-y-4">
            <div className="relative">
              <input
                type="file"
                accept={accept}
                {...register(name, {
                  required: required ? `${label} is required` : false,
                })}
                className="hidden"
                id={name}
                multiple={multiple}
              />
              <label
                htmlFor={name}
                className="cursor-pointer block w-full p-6 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-blue-500 transition-colors bg-gray-50 hover:bg-gray-100"
              >
                <div className="space-y-2">
                  <div className="text-blue-500">
                    <svg className="mx-auto h-12 w-12" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium text-blue-600 hover:text-blue-500">
                      Upload images
                    </span>
                    {' or drag and drop'}
                  </div>
                  <p className="text-xs text-gray-500">
                    PNG, JPG, GIF up to 10MB each
                  </p>
                </div>
              </label>
            </div>

            {preview ? (
              preview
            ) : (
              watchFields[name]?.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {Array.from(watchFields[name]).map((file, index) => (
                    <div key={index} className="relative group">
                      <div className="aspect-square w-full overflow-hidden rounded-lg bg-gray-100">
                        <img
                          src={getFileBlobUrl(file, name, index)}
                          alt={`Upload preview ${index + 1}`}
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            // Clean up blob URL for the file being removed
                            cleanupBlobUrl(file, name, index);
                            
                            const updatedFiles = Array.from(watchFields[name])
                              .filter((_, i) => i !== index);
                            setValue(name, updatedFiles);
                          }}
                          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <span className="sr-only">Remove image</span>
                          <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-500 truncate">
                        {file.name}
                      </p>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        );

      case 'dropdown':
        return component;

      case 'keywords-input':
        return (
          <KeywordsInput
            control={control}
            name={name}
            required={required}
            label={label}
            onChange={(value) => {
              if (onChange) onChange(value);
            }}
          />
        );
      case 'pills-input':
        return (
          <Controller
            control={control}
            name={name}
            rules={{ required: required ? `${label} is required` : false }}
            render={({ field }) => (
              <PillsInput
                value={field.value || []}
                onChange={field.onChange}
                placeholder={placeholder}
              />
            )}
          />
        );

      case 'business-dropdown':
        return (
          <BusinessDropdown
            control={control}
            name={name}
            required={required}
            label={label}
            initialValue={defaultValues[name] || []}
            onChange={(value) => {
              if (onChange) onChange(value);
            }}
          />
        );

      
      default:
        return (
          <input
            type={type}
            {...commonProps}
          />
        );
    }
  };

  return (
    <form onSubmit={handleSubmit(submitHandler)} className="space-y-4">
      {error && <div className="text-red-500 mb-4">{error}</div>}
      
      {fields.map((field) => (
        <div key={field.name} className="mb-4">
          <label htmlFor={field.name} className="block mb-2 font-medium">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {renderField(field)}
          {errors[field.name] && (
            <span className="text-red-500 text-sm mt-1">
              {errors[field.name].message}
            </span>
          )}
        </div>
      ))}

      <button 
        type="submit" 
        disabled={loading}
        className={`w-full px-4 py-2 rounded text-white ${
          loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'
        }`}
      >
        {loading ? 'Loading...' : submitText}
      </button>
    </form>
  );
};

export default ReactForm;
